import React from 'react';
import { Participant } from 'twilio-video';
import { Tooltip } from '@mui/material';
import useParticipantNetworkQualityLevel from '../../hooks/useParticipantNetworkQualityLevel/useParticipantNetworkQualityLevel';
import { styled, keyframes } from '@mui/material/styles';

const pulse = keyframes`
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
  100% {
    opacity: 1;
  }
`;

const OuterContainer = styled('div')<{ isPoor?: boolean }>(({ isPoor }) => ({
  width: '2em',
  height: '2em',
  padding: '0.9em',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(0, 0, 0, 0.5)',
  borderRadius: '4px',
  cursor: 'help',
  ...(isPoor && {
    animation: `${pulse} 1.5s ease-in-out infinite`,
  }),
}));

const InnerContainer = styled('div')({
  display: 'flex',
  alignItems: 'flex-end',
  '& div': {
    width: '2px',
    marginRight: '1px',
    transition: 'background-color 0.3s ease, height 0.3s ease',
    '&:not(:last-child)': {
      borderRight: 'none',
    },
  },
});

const STEP = 3;
const BARS_ARRAY = [0, 1, 2, 3, 4];

// Color mapping based on quality level
const getQualityColor = (level: number): string => {
  if (level >= 4) return '#4caf50'; // Green - Excellent
  if (level === 3) return '#8bc34a'; // Light Green - Good
  if (level === 2) return '#ff9800'; // Orange - Fair
  return '#f44336'; // Red - Poor
};

const getQualityLabel = (level: number): string => {
  if (level >= 4) return 'Excellent';
  if (level === 3) return 'Good';
  if (level === 2) return 'Fair';
  if (level === 1) return 'Poor';
  return 'Very Poor';
};

const getQualitySuggestion = (level: number): string => {
  if (level >= 4) return 'Your connection is excellent.';
  if (level === 3) return 'Your connection is good.';
  if (level === 2) return 'Consider moving closer to your router or reducing video quality.';
  if (level === 1) return 'Your connection is poor. Try disabling video or switching networks.';
  return 'Very poor connection. You may experience disconnections.';
};

interface NetworkQualityLevelProps {
  participant: Participant;
  showTooltip?: boolean;
}

export default function NetworkQualityLevel({ participant, showTooltip = true }: NetworkQualityLevelProps) {
  const networkQualityLevel = useParticipantNetworkQualityLevel(participant);

  if (networkQualityLevel === null) return null;

  const qualityColor = getQualityColor(networkQualityLevel);
  const qualityLabel = getQualityLabel(networkQualityLevel);
  const qualitySuggestion = getQualitySuggestion(networkQualityLevel);
  const isPoor = networkQualityLevel <= 1;

  const content = (
    <OuterContainer isPoor={isPoor}>
      <InnerContainer>
        {BARS_ARRAY.map(level => (
          <div
            key={level}
            style={{
              height: `${STEP * (level + 1)}px`,
              background: networkQualityLevel > level ? qualityColor : 'rgba(255, 255, 255, 0.2)',
            }}
          />
        ))}
      </InnerContainer>
    </OuterContainer>
  );

  if (!showTooltip) return content;

  return (
    <Tooltip
      title={
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: 600, marginBottom: '4px' }}>
            Network Quality: {qualityLabel} ({networkQualityLevel}/5)
          </div>
          <div style={{ fontSize: '0.85em', opacity: 0.9 }}>
            {qualitySuggestion}
          </div>
        </div>
      }
      arrow
      placement="top"
    >
      {content}
    </Tooltip>
  );
}
